import React from "react";
import PolicyLayout from "../../../components/PolicyLayout";
import s from "../../../styles/PolicyLayout.module.css";

export default function SmartCaloriePrivacy() {
  return (
    <PolicyLayout
      title="privacy policy"
      appName="Ai Calorie counter: food tracker"
      date="NOVEMBER 2025"
    >
      <div className={s.title}>1) Introduction and Scope</div>
      <p>
        This Privacy Policy explains how “Smart Calorie — AI Food Tracker” (the
        “App”) collects, uses, stores, and discloses information about users.
        The App helps you estimate a daily calorie goal, log meals using a
        third-party nutrition database, track macronutrients, and view daily
        progress with a simple UI. This Policy covers onboarding, entry of basic
        profile inputs to calculate a daily target, food search via an external
        API, local storage of logs, subscriptions and entitlements, analytics
        oriented to reliability, crash diagnostics, notifications (if enabled),
        and support. We design the App with a local-first approach so meal logs
        can remain on your device by default and are shared only when you
        explicitly export or sync using external services of your choice. By
        using the App, you acknowledge this Policy and agree to its terms. If
        you do not agree, uninstall the App.
      </p>

      <div className={s.title}>2) Controller and Contact Information</div>
      <p>
        The controller that determines the purposes and means of processing for
        the App is Aleksandra Rakitina. You may contact alexwadek@gmail.com for
        questions about this Policy or to exercise rights such as access,
        deletion, correction, restriction, objection, or data portability. To
        protect your account and data, we will verify your identity before
        acting on requests, typically by confirming control of the email used in
        the App or providing another minimal verification step. We keep privacy
        correspondence for compliance and recordkeeping where permitted by law.
        If your inquiry concerns platform subscription management or refunds, we
        will direct you to the relevant device account settings while addressing
        privacy matters within our remit.
      </p>

      <div className={s.title}>
        3) Key Principles: Local-First, Minimization, Transparency
      </div>
      <p>
        We collect only what is needed to deliver requested features. Local-first
        means your meal entries, daily totals, and preferences are stored on
        your device unless you explicitly export or back them up with
        third-party services outside our control. Minimization means we avoid
        collecting sensitive data unrelated to core functionality and do not
        sell Personal Data. Transparency means we disclose how third-party
        providers (nutrition database, analytics, subscriptions, crash
        diagnostics, notifications) process limited information under our
        instructions. Where consent is legally required (for example,
        notifications), we request it in clear prompts and provide easy controls
        to change your choices later without degrading basic functionality
        beyond what is technically necessary.
      </p>

      <div className={s.title}>4) Definitions</div>
      <p>
        “Personal Data” means information relating to an identified or
        identifiable person, including identifiers from authentication and
        billing platforms. “Profile Inputs” are the values you provide during
        onboarding (goal selection, sex, age, height, weight, activity level)
        used to estimate a daily calorie target via well-known formulas. “Food
        Data” refers to nutrition information returned by the third-party API
        for items you search and add. “Daily Logs” are your per-day entries and
        calculated totals (calories and macronutrients) saved locally.
        “Technical Data” includes device model, OS/app versions, locale, and
        diagnostic information. “Processing” includes collection, storage, use,
        transmission, export, and deletion. “Service Providers” are third
        parties acting on our instructions to supply authentication,
        subscriptions, analytics, crash reporting, notifications, and the
        food/nutrition database.
      </p>

      <div className={s.title}>5) Categories of Data We Process</div>
      <p>
        Depending on your choices, we may process: (a) account and authentication
        data if you sign in (email, provider IDs, display name where available)
        to support entitlements and restoration of purchases; (b) Profile Inputs
        (goal, sex, age, height, weight, activity) and derived daily targets
        that remain local by default; (c) Daily Logs including items added,
        serving sizes, calories, and macronutrients; (d) Food Data retrieved
        from the nutrition API for your search queries; (e) subscription and
        entitlement metadata from RevenueCat (product identifiers, trial
        eligibility, renewal/expiration/cancellation statuses); (f) analytics
        and crash diagnostics (event counters, anonymized error codes,
        performance metrics); and (g) notification tokens and preferences if you
        opt in. We do not receive your payment card numbers.
      </p>

      <div className={s.title}>6) Sources of Data and Voluntary Provision</div>
      <p>
        Most information originates from you during onboarding and when logging
        meals. Additional data arises automatically from App operation (for
        example, event counts used to measure stability or identify crashes).
        Food Data is supplied by a third-party nutrition database in response to
        your queries. Providing certain information is optional, but some
        features require it: without Profile Inputs the App cannot estimate a
        daily target; without network access the nutrition search may be
        unavailable; without authentication some entitlement features (such as
        restoring purchases across devices) may not function; without
        notification permission push messages cannot be delivered. You can
        change inputs, revoke permissions, or sign out at any time.
      </p>

      <div className={s.title}>
        7) Onboarding Inputs and Daily Target Calculation
      </div>
      <p>
        During onboarding, you may provide goal, sex, age, height, weight, and
        activity level. The App uses these inputs to compute an estimated daily
        calorie target (e.g., based on the Mifflin–St Jeor equation with
        activity multipliers) and to split suggested macronutrient ranges if you
        choose. These inputs and outcomes are stored locally using app-scoped
        storage (Hive or SharedPreferences). You can edit inputs later in
        Settings to recalculate targets. The App does not share Profile Inputs
        with advertising networks. We encourage accurate entries to improve
        usefulness, but you remain in control of changes; edits take effect
        prospectively and do not retroactively change historical totals already
        saved on your device.
      </p>

      <div className={s.title}>
        8) Meal Search, Food Database Queries, and Caching
      </div>
      <p>
        When you search for foods (for example, “chicken salad”), the App sends
        your query to a nutrition database provider over HTTPS using required
        API headers (application ID/key) configured in the App. The provider
        responds with Food Data such as calories and macronutrients. We do not
        attach unrelated identifiers to these requests, and we do not send your
        Profile Inputs with the search. To improve speed, the App may cache a
        subset of recent results locally (for example, your last ten searches).
        Cached items can be cleared by deleting app data or using system storage
        controls. We do not operate our own nutrition service and do not
        re-sell Food Data.
      </p>

      <div className={s.title}>
        9) Daily Logs, Local Storage, and History Limits
      </div>
      <p>
        Your added items and daily totals are stored in a local database within
        the App’s sandbox. In the free tier, logging may be limited to a small
        number of active days (for example, three) and history views may be
        restricted. Premium users can unlock extended or unlimited history
        according to plan. Deleting an item adjusts the daily totals immediately
        and removes the corresponding local record. Uninstalling the App removes
        the sandbox and stored logs. If you exported data to a third-party
        destination (for example, a file or cloud drive), deleting the App will
        not remove those external copies; manage them in the destination
        service.
      </p>

      <div className={s.title}>
        10) Subscriptions, Trials, Pricing, and Entitlements
      </div>
      <p>
        Monetization uses in-app purchases via RevenueCat and platform billing. A
        weekly plan (e.g., $4.99/week) offers a 3-day free trial and unlocks
        features such as unlimited days/history and prior-day navigation. We
        receive entitlement metadata (product IDs, trial eligibility, renewal
        outcomes, expiration dates, cancellation status) to grant or revoke
        access within the App. We do not receive payment card details. Manage or
        cancel subscriptions in your device account settings; refunds, grace
        periods, and price changes follow platform rules and applicable law. If
        an attempt is made to access premium features after the free threshold,
        the paywall may be shown as described within the App.
      </p>

      <div className={s.title}>
        11) Analytics, Performance, and Crash Diagnostics
      </div>
      <p>
        We use Firebase Analytics and Crashlytics to understand reliability,
        performance, and feature adoption—not to build behavioral advertising
        profiles. Typical events include onboarding completion, add_food,
        day_logged, open_paywall, trial_started, purchase_success, as well as
        anonymized error codes and device/OS versions. Where feasible, analytics
        are aggregated or pseudonymized, and we avoid including your Daily Logs
        or Profile Inputs in event payloads. You can limit analytics using
        platform privacy controls where available, noting this may reduce our
        ability to troubleshoot rare device-specific issues or regressions after
        updates. We do not combine analytics with third-party ad networks for
        targeted ads.
      </p>

      <div className={s.title}>12) Notifications and In-App Messages</div>
      <p>
        With your permission, the App may send reminders (for example, “Log
        lunch”) or service messages related to subscription status. Delivery
        requires a device token that we and our providers use solely to route
        messages to your device. You can disable notifications in system
        settings and, where provided, in-app toggles. We avoid including
        detailed health or nutrition contents in notification bodies. Some
        service-critical communications permitted by law (e.g., security or
        billing notices) may still be delivered. If you uninstall the App, the
        platform typically invalidates your token, though brief propagation
        delays may occur before deactivation is complete.
      </p>

      <div className={s.title}>13) What We Do Not Collect or Share</div>
      <p>
        We do not sell Personal Data. We do not collect payment card numbers. We
        do not transmit your entire Daily Logs to advertising networks. We do
        not require precise geolocation, contacts, or photos to use core
        features. We do not run our own social graph and do not share your Daily
        Logs with social networks. If you choose to export or share data using
        third-party services (for example, files, cloud drives, messaging apps),
        those services’ privacy practices apply and are outside our control. We
        do not accept responsibility for copies you voluntarily share externally.
      </p>

      <div className={s.title}>14) Legal Bases and Purposes (EEA/UK)</div>
      <p>
        For users in the EEA/UK, our legal bases include: performance of a
        contract (core functionality such as onboarding, calculating targets,
        logging, and restoring entitlements); legitimate interests (security,
        fraud prevention, stability, and product improvement) balanced against
        your rights through minimization and opt-outs where available; consent
        (notifications and certain analytics categories where required); and
        legal obligations (recordkeeping and compliance with lawful requests).
        If our purposes materially change, we will update this Policy and, where
        required, seek additional consent before using data in a new way. We do
        not use your data for third-party behavioral advertising.
      </p>

      <div className={s.title}>15) Security Measures</div>
      <p>
        We implement appropriate administrative, technical, and organizational
        safeguards. Communications with providers use HTTPS; local storage
        resides in the app sandbox; sensitive tokens are stored using platform
        mechanisms. We apply least-privilege access for operational tools,
        rotate credentials, separate development and production environments, and
        avoid using production Personal Data for testing. While no system is
        perfectly secure, the local-first design reduces exposure by keeping
        Daily Logs on your device unless you choose to export. If we become
        aware of a security incident affecting Personal Data we control (for
        example, subscription or support records), we will investigate promptly
        and notify as required by law.
      </p>

      <div className={s.title}>16) Data Retention and Deletion</div>
      <p>
        We retain Personal Data only as long as needed to provide the service,
        maintain security, comply with legal obligations, and resolve disputes.
        Local data (Profile Inputs and Daily Logs) persists until you delete
        entries or uninstall the App. Subscription and entitlement metadata may
        be retained longer for accounting and fraud prevention, typically in
        pseudonymized or aggregated form. Crash and performance logs are kept
        for trend windows then deleted or de-identified. Verified deletion
        requests for server-side records we control (for example, support
        emails) are handled within a reasonable time, noting that distributed
        systems and backups can introduce short delays before full purge.
      </p>

      <div className={s.title}>17) International Data Transfers</div>
      <p>
        Depending on your location and our providers’ locations, your data may
        be processed in jurisdictions with different data protection rules.
        Where required, we rely on appropriate safeguards for cross-border
        transfers, such as contractual protections and platform mechanisms.
        Regardless of location, our providers must implement security measures
        consistent with this Policy and applicable law. Typical transfers relate
        to analytics, crash reporting, subscriptions, and nutrition database
        queries. You may contact us for information about relevant transfer
        mechanisms and, where appropriate, summaries of key contractual
        commitments that do not reveal confidential commercial information.
      </p>

      <div className={s.title}>18) Your Rights and Choices</div>
      <p>
        Subject to law, you may have rights to access, rectify, delete, or port
        Personal Data; to restrict or object to certain processing; and to
        withdraw consent where processing relies on consent. In-app controls let
        you edit onboarding inputs, delete items, clear recent searches, and
        manage notifications. Subscription management (including cancellation and
        refunds) occurs in platform account settings. To exercise other rights,
        email alexwadek@gmail.com from your sign-in address. We will verify your
        identity before responding. If we cannot fulfill a request due to legal
        or technical limits, we will explain the reason unless prohibited by
        law.
      </p>

      <div className={s.title}>19) Children’s Privacy</div>
      <p>
        The App is not directed to children, and we do not knowingly collect
        Personal Data from users under the age where parental consent is
        required by applicable law. If you believe a minor used the App without
        proper authorization, contact alexwadek@gmail.com and we will remove
        information and terminate access where applicable. Parents and guardians
        can use device-level controls to limit purchases and notifications. If we
        learn that we processed data from a child without appropriate consent, we
        will promptly delete such data consistent with legal requirements.
        Future education-specific features, if any, will include appropriate
        disclosures and consent flows.
      </p>

      <div className={s.title}>
        20) Do Not Track, Identifiers, and SDK Disclosures
      </div>
      <p>
        As a native mobile app, we do not use browser cookies. SDKs may store
        identifiers needed for authentication, subscriptions, analytics, crash
        diagnostics, and notifications. These identifiers function similarly to
        cookies by enabling sessions, entitlement checks, and measurement. The
        App does not rely on advertising identifiers for targeted ads. Browser
        “Do Not Track” signals generally do not apply to native apps; nonetheless,
        we keep SDK usage scoped to necessary purposes and avoid combining
        identifiers across services for unrelated marketing. Platform settings
        may allow you to reset device identifiers or limit certain measurements.
      </p>

      <div className={s.title}>
        21) Accuracy, Health-Related Caution, and User Responsibility
      </div>
      <p>
        Calculated targets and nutrition data are estimates intended for general
        wellness tracking, not medical advice. The App does not diagnose, treat,
        or prescribe. Always consider your personal context and consult qualified
        professionals for individualized guidance. You remain responsible for how
        you use the information and whether it is appropriate for your needs. The
        App depends on third-party Food Data and user inputs, which can contain
        inaccuracies; you can edit items or portion sizes to better reflect your
        meals. This section concerns product expectations and does not limit
        statutory privacy rights described elsewhere in this Policy.
      </p>

      <div className={s.title}>
        22) Third-Party Providers and Disclosures
      </div>
      <p>
        To operate the App, we share limited data with vetted providers acting on
        our instructions: a nutrition database for food search queries, RevenueCat
        for subscriptions and entitlements, Firebase Analytics/Crashlytics for
        measurement and stability, and FCM for notifications (if enabled).
        Providers are bound by terms requiring appropriate security and
        prohibiting use of Personal Data for their independent marketing. We may
        disclose information when legally required by competent authorities after
        validating scope and jurisdiction. In a business transition affecting the
        App, successor entities will honor this Policy or provide notice of
        material changes so you can make informed choices.
      </p>

      <div className={s.title}>23) Export and External Destinations</div>
      <p>
        If you export or share data (for example, a CSV, PDF, or text file created
        by the App or a screenshot), exported copies are controlled by the
        destination apps and services you select, which have their own privacy
        practices. Deleting items in the App does not remove exported files
        elsewhere. Review the destination service’s terms and consider access
        controls—especially on shared devices—before sharing sensitive
        information such as weight or goal details. The App does not embed unique
        tracking identifiers into exported documents unless you explicitly request
        a feature that requires them.
      </p>

      <div className={s.title}>
        24) Platform Compliance and Review Transparency
      </div>
      <p>
        For clarity to users and reviewers, we disclose that: (a) Profile Inputs
        and Daily Logs are stored locally by default; (b) nutrition searches are
        sent to a third-party API solely to return Food Data; (c) subscriptions
        use platform billing via RevenueCat; (d) analytics and crash logs are
        oriented to stability, not content; and (e) paywall may appear after
        free-tier limits (for example, after three days of logging) or when
        attempting premium history. Any future feature that changes data flows
        (for example, optional cloud backup) will be disabled by default and will
        include in-app disclosures and updated Policy text before activation.
      </p>

      <div className={s.title}>
        25) Data Retention Windows and Backups
      </div>
      <p>
        Local data persists on your device until deleted or until the App is
        uninstalled. Provider-side metadata (for example, subscription events)
        may be retained for accounting, fraud prevention, and legal obligations
        in line with provider policies and applicable law. Analytics and crash
        diagnostics are retained for operational trend windows before deletion or
        de-identification. When you request deletion of server-side records we
        control, we process your request within a reasonable timeframe; note that
        provider backups and replication may introduce short delays before all
        copies are purged.
      </p>

      <div className={s.title}>26) Security Incident Response</div>
      <p>
        If we become aware of a security incident affecting Personal Data we
        control, we will investigate promptly, take appropriate remediation steps,
        and notify affected users and regulators where required by law. Because
        Daily Logs are local by default and nutrition queries are transient
        requests to a provider, the scope of any remote incident is generally
        limited to non-content data such as subscription or support records. You
        can further protect your information by using strong device passcodes,
        enabling OS updates, and keeping the App current to receive security and
        reliability improvements.
      </p>

      <div className={s.title}>27) Changes to This Policy</div>
      <p>
        We may update this Policy to reflect operational, legal, or regulatory
        developments. The “Last Updated” date will change when updates occur.
        Material changes that affect your rights or our data uses will be
        highlighted in-app, and where required we will seek consent for new
        purposes. Continued use of the App after changes take effect signifies
        acceptance. We encourage periodic review, especially when enabling new
        features or modifying inputs that affect calculated targets. If you
        disagree with updates, you may stop using the App and request deletion of
        account-linked records we control, subject to verification and applicable
        retention obligations.
      </p>

      <div className={s.title}>
        28) How to Submit Privacy Requests and Verification Steps
      </div>
      <p>
        To exercise your rights, email alexwadek@gmail.com from the address used
        to sign in (if applicable) and specify the request type (access,
        deletion, correction, restriction, objection, portability). Include
        helpful context such as device model and app version. We verify identity
        to prevent unauthorized access or deletion and may request limited
        additional information or a confirmation code. Where feasible, data is
        provided in a portable, machine-readable format. We may decline or limit
        requests when permitted by law—for example, if fulfilling a request would
        compromise security, interfere with legal obligations, or infringe the
        privacy of others—and we will explain our reasoning unless prohibited.
      </p>
    </PolicyLayout>
  );
}
